import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import { dateTime } from '../lib/format.js';
import {
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Panel,
  StatusBadge,
  Toggle,
} from '../components/ui.js';
import '../screens-2c.css';

/**
 * Karta „Google Analytics 4“ v Nastavení → Napojení (D-234 až D-237). Klíč servisního účtu je
 * jen v prostředí serveru: karta ukazuje „nastaveno / nenastaveno“, maskovaný e-mail účtu a
 * výběr z nalezených properties. Nic tajného se tu nezadává ani nezobrazuje.
 */

interface Property {
  id: string;
  propertyId: string;
  displayName: string;
  accountName: string | null;
  timeZone: string | null;
  enabled: boolean;
  brand: string | null;
  lastPulledDate: string | null;
  backoffUntil: string | null;
  inBackoff: boolean;
  consecutiveErrors: number;
  lastError: string | null;
}

interface Ga4Data {
  enabledSetting: boolean;
  source: 'env' | 'none';
  configured: boolean;
  configError?: string;
  account: {
    clientEmail: string;
    projectId: string | null;
    lastCheckAt: string | null;
    lastCheckOk: boolean | null;
    lastCheckError: string | null;
  } | null;
  summary: { total: number; enabled: number; failing: number; backoff: number };
  items: Property[];
  page: { page: number; pageSize: number; total: number };
}

const PAGE_SIZE = 50;

function state(p: Property): JSX.Element {
  if (p.inBackoff && p.backoffUntil) {
    return (
      <StatusBadge tone="warn" icon="alert">
        Odklad do {dateTime(p.backoffUntil)}
      </StatusBadge>
    );
  }
  if (p.consecutiveErrors > 0) {
    return (
      <StatusBadge tone="bad" icon="alert">
        Chyba
      </StatusBadge>
    );
  }
  return p.enabled ? (
    <StatusBadge tone="ok">V pořádku</StatusBadge>
  ) : (
    <StatusBadge tone="neu">Vypnuto</StatusBadge>
  );
}

export function Ga4Card({ canWrite }: { canWrite: boolean }): JSX.Element {
  const client = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'' | 'true' | 'false' | 'error'>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBrand, setBulkBrand] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery<Ga4Data>({
    queryKey: ['ga4', search, filter, page],
    queryFn: () =>
      api.get(
        `/api/ga4${qs({ search: search || undefined, enabled: filter || undefined, page, pageSize: PAGE_SIZE })}`,
      ),
  });
  const refresh = (): void => void client.invalidateQueries({ queryKey: ['ga4'] });
  const fail = (err: Error): void => {
    setNotice(null);
    setError(err.message);
  };
  const done = (text: string): void => {
    setError(null);
    setNotice(text);
    refresh();
  };

  const test = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; properties?: number; message?: string }>('/api/ga4/test'),
    onSuccess: (r) =>
      r.ok
        ? done(`Spojení funguje. Nalezeno ${r.properties ?? 0} properties.`)
        : (refresh(), fail(new Error(r.message ?? 'Spojení selhalo.'))),
    onError: fail,
  });
  const discover = useMutation({
    mutationFn: () => api.post<{ found: number; added: number }>('/api/ga4/discover'),
    onSuccess: (r) => done(`Nalezeno ${r.found} properties, z toho ${r.added} nových.`),
    onError: fail,
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.patch('/api/ga4/settings', { enabled }),
    onSuccess: () => done('Uloženo.'),
    onError: fail,
  });
  const change = useMutation({
    mutationFn: (b: { ids: string[]; enabled?: boolean; brand?: string | null }) =>
      api.patch('/api/ga4/properties', b),
    onSuccess: () => done('Uloženo.'),
    onError: fail,
  });

  if (query.isLoading) return <LoadingState what="Google Analytics 4" />;
  if (query.isError) {
    const off = query.error instanceof ApiError && query.error.code === 'layer_disabled';
    return off ? (
      <Panel title="Google Analytics 4" subtitle="Denní agregáty návštěv ze všech webů.">
        <EmptyState title="Vrstva „Chování na webu a GA4“ není zapnutá">
          Zapíná se v Nastavení → Vlny nasazení (vlna 6).
        </EmptyState>
      </Panel>
    ) : (
      <ErrorState
        error={query.error}
        title="Google Analytics 4 se nepodařilo načíst"
        onRetry={() => void query.refetch()}
      />
    );
  }
  const data = query.data;
  if (!data) return <LoadingState />;

  const chip = !data.configured ? (
    <span data-testid="ga4-chip">
      <StatusBadge tone="neu">Nenapojeno</StatusBadge>
    </span>
  ) : data.account?.lastCheckOk === false ? (
    <span data-testid="ga4-chip">
      <StatusBadge tone="bad" icon="alert">
        Chyba
      </StatusBadge>
    </span>
  ) : (
    <span data-testid="ga4-chip">
      <StatusBadge tone="ok" icon="check">
        Napojeno z prostředí
      </StatusBadge>
    </span>
  );
  const ids = [...selected];
  const lastPulled = data.items
    .map((i) => i.lastPulledDate)
    .filter(Boolean)
    .sort()
    .pop();
  const pages = Math.max(1, Math.ceil(data.page.total / PAGE_SIZE));

  return (
    <div data-testid="ga4-card">
      <Panel
        title="Google Analytics 4"
        subtitle="Hub jednou denně stahuje agregované návštěvy (z e-mailu a celkem) za všechny weby. Jen čte; osobní údaje z GA4 nepřicházejí."
        action={chip}
      >
        {error ? <ErrorState error={new Error(error)} title="Akce se nepodařila" /> : null}
        {notice ? (
          <p className="note" role="status">
            {notice}
          </p>
        ) : null}

        <p className="note">
          Klíč servisního účtu Google se nastavuje v prostředí serveru (
          <code>GA4_SERVICE_ACCOUNT_FILE</code> nebo <code>GA4_SERVICE_ACCOUNT_JSON</code>
          ), nikdy v administraci ani v databázi. E-mail servisního účtu přidejte v GA4 (Správce →
          Správa přístupu) jako <b>Čtenáře</b> účtu nebo property.
        </p>
        {data.source === 'none' ? (
          <p className="note">Klíč v prostředí zatím není nastaven, hub nic nestahuje.</p>
        ) : null}
        {data.configError ? (
          <ErrorState
            error={new Error(data.configError)}
            title="Klíč v prostředí je nepoužitelný"
          />
        ) : null}
        {data.account ? (
          <p className="note">
            Servisní účet <code>{data.account.clientEmail}</code>
            {data.account.projectId ? <> (projekt {data.account.projectId})</> : null}.{' '}
            {data.account.lastCheckAt
              ? data.account.lastCheckOk
                ? `Spojení ověřeno ${dateTime(data.account.lastCheckAt)}.`
                : `Spojení ${dateTime(data.account.lastCheckAt)} selhalo: ${data.account.lastCheckError ?? 'chyba'}`
              : 'Spojení zatím neověřeno.'}
          </p>
        ) : null}

        <div className="s2c-actions">
          <label className="row">
            <Toggle
              checked={data.enabledSetting}
              disabled={!canWrite || !data.configured || setEnabled.isPending}
              onChange={(v) => setEnabled.mutate(v)}
              label="Stahovat data z GA4"
            />{' '}
            Stahovat data z GA4
          </label>
          {canWrite ? (
            <>
              <button
                type="button"
                className="btn"
                data-testid="ga4-test"
                disabled={!data.configured || test.isPending}
                onClick={() => test.mutate()}
              >
                {test.isPending ? 'Ověřuji…' : 'Otestovat spojení'}
              </button>
              <button
                type="button"
                className="btn"
                data-testid="ga4-discover"
                disabled={!data.configured || discover.isPending}
                onClick={() => discover.mutate()}
              >
                {discover.isPending ? 'Hledám…' : 'Najít properties'}
              </button>
            </>
          ) : (
            <span className="note">Nastavení smí měnit jen správce.</span>
          )}
        </div>

        <div data-testid="ga4-properties">
          <p className="note">
            Properties: {data.summary.total}, stahuje se {data.summary.enabled}, s chybou{' '}
            {data.summary.failing}, v odkladu {data.summary.backoff}.
            {lastPulled ? ` Naposledy staženo za den ${lastPulled}.` : ''}
          </p>
          <div className="s2c-form">
            <Field label="Hledat">
              <input
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </Field>
            <Field label="Zobrazit">
              <select
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value as typeof filter);
                  setPage(1);
                }}
              >
                <option value="">Všechny</option>
                <option value="true">Zapnuté</option>
                <option value="false">Vypnuté</option>
                <option value="error">S chybou</option>
              </select>
            </Field>
          </div>
          {canWrite && ids.length > 0 ? (
            <div className="s2c-actions">
              <span>Vybráno {ids.length}:</span>
              <button
                type="button"
                className="btn"
                onClick={() => change.mutate({ ids, enabled: true })}
              >
                Zapnout vybrané
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => change.mutate({ ids, enabled: false })}
              >
                Vypnout vybrané
              </button>
              <BrandSelect value={bulkBrand} onChange={setBulkBrand} allLabel="Bez značky" />
              <button
                type="button"
                className="btn"
                onClick={() => change.mutate({ ids, brand: bulkBrand || null })}
              >
                Nastavit značku vybraným
              </button>
            </div>
          ) : null}
          {data.items.length === 0 ? (
            <EmptyState title="Žádné properties">
              {data.summary.total === 0
                ? 'Nejdřív klikněte na „Najít properties“ (potřebuje klíč v prostředí a roli Čtenář v GA4).'
                : 'Zadanému hledání nic neodpovídá.'}
            </EmptyState>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th aria-label="Výběr" />
                    <th>Název</th>
                    <th>Účet</th>
                    <th>ID</th>
                    <th>Časové pásmo</th>
                    <th>Stahovat</th>
                    <th>Značka</th>
                    <th>Poslední stažení</th>
                    <th>Stav</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Vybrat ${p.displayName}`}
                          disabled={!canWrite}
                          checked={selected.has(p.id)}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(p.id);
                            else next.delete(p.id);
                            setSelected(next);
                          }}
                        />
                      </td>
                      <td>{p.displayName}</td>
                      <td>{p.accountName ?? ''}</td>
                      <td>
                        <code>{p.propertyId}</code>
                      </td>
                      <td>{p.timeZone ?? ''}</td>
                      <td>
                        <Toggle
                          checked={p.enabled}
                          disabled={!canWrite}
                          label={`Stahovat ${p.displayName}`}
                          onChange={(v) => change.mutate({ ids: [p.id], enabled: v })}
                        />
                      </td>
                      <td>
                        <BrandSelect
                          value={p.brand ?? ''}
                          allLabel="Bez značky"
                          onChange={(code) => change.mutate({ ids: [p.id], brand: code || null })}
                        />
                      </td>
                      <td>{p.lastPulledDate ?? 'zatím ne'}</td>
                      <td>
                        {state(p)}
                        {p.lastError ? <div className="note">{p.lastError}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pages > 1 ? (
            <div className="s2c-actions">
              <button
                type="button"
                className="btn"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Předchozí
              </button>
              <span>
                Strana {page} z {pages}
              </span>
              <button
                type="button"
                className="btn"
                disabled={page >= pages}
                onClick={() => setPage(page + 1)}
              >
                Další
              </button>
            </div>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}
